/**
 * App — Thin router shell.
 *
 * Public routes (landing, login, free explore) render directly. All
 * authenticated routes go through AuthenticatedShell, which keeps
 * MapPage mounted across navigation to /alerts, /research, /admin,
 * /api-keys, and /support so the map view is preserved instead of
 * reset every time.
 */

import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { AuthGate } from './components/AuthGate';
import { AuthenticatedShell } from './components/AuthenticatedShell';
import { initAnalytics } from './analytics';
import { usePageTracking } from './hooks/usePageTracking';

// Public routes — marketing splash + dedicated login. Kept eager so the
// initial paint doesn't wait on a chunk fetch on the very first visit,
// which is the most-shown page.
import { LandingPage } from './components/LandingPage';
import { LoginPage } from './components/LoginPage';

const FreeMapPage = React.lazy(() => import('./components/FreeMapPage'));

const LoadingFallback = () => (
  <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#0a1628', color: '#94a3b8' }}>
    Loading...
  </div>
);

// Hook-only component — has to live inside <Router> so usePageTracking
// can call useLocation(). Renders nothing.
const PageTracker: React.FC = () => {
  usePageTracking();
  return null;
};

export default function App() {
  // Boot GA4 once on mount. initAnalytics is a no-op when the server
  // hasn't set GA4_MEASUREMENT_ID (local dev without a property), so
  // this doesn't need to be gated per environment.
  useEffect(() => { void initAnalytics(); }, []);

  // Router must wrap AuthGate so AuthGate can call useLocation() to gate
  // the briefing modal on the map view. Login / ChangePassword inside
  // AuthGate don't depend on routing, so they're unaffected.
  return (
    <Router>
      <PageTracker />
      <AuthGate>
        <React.Suspense fallback={<LoadingFallback />}>
          <Routes>
            {/* Public marketing / auth routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            {/* Free tier — Venezuela-only slice of the map, no login required.
                Path is allow-listed in AuthGate's PUBLIC_PATHS. */}
            <Route path="/explore/venezuela" element={<FreeMapPage />} />
            {/* Protected app routes — the shell keeps MapPage mounted
                across navigation so the map view survives trips to
                /alerts, /research, /admin, etc. */}
            <Route path="*" element={<AuthenticatedShell />} />
          </Routes>
        </React.Suspense>
      </AuthGate>
    </Router>
  );
}
